import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { PORTFOLIO, PORTFOLIO_FILTERS } from "@/lib/siteData";

export default function Portfolio() {
    const [filter, setFilter] = useState("All");
    const [active, setActive] = useState(null);

    const items = useMemo(
        () =>
            filter === "All"
                ? PORTFOLIO
                : PORTFOLIO.filter((p) => p.category === filter),
        [filter]
    );

    return (
        <section
            id="portfolio"
            data-testid="portfolio"
            className="relative py-24 md:py-32 bg-secondary/40"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <div className="flex items-center gap-3 mb-5">
                            <span className="divider-gold" />
                            <span className="uppercase tracking-[0.28em] text-xs text-gold font-medium">
                                Portfolio
                            </span>
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight max-w-2xl">
                            Selected works, rendered in cinematic detail.
                        </h2>
                    </div>

                    <div
                        role="tablist"
                        className="flex flex-wrap gap-2 no-scrollbar overflow-x-auto"
                        data-testid="portfolio-filters"
                    >
                        {PORTFOLIO_FILTERS.map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                data-testid={`filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
                                className={`px-4 h-9 text-xs uppercase tracking-[0.18em] rounded-full border transition ${
                                    filter === f
                                        ? "bg-gold text-white border-gold"
                                        : "hairline text-foreground/80 hover:border-gold"
                                }`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4">
                    {items.map((p, idx) => (
                        <motion.button
                            layout
                            key={p.id}
                            data-testid={`portfolio-item-${p.id}`}
                            onClick={() => setActive(p)}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.4, delay: (idx % 4) * 0.05 }}
                            className={`relative group overflow-hidden rounded-xl border hairline ${
                                p.span || ""
                            }`}
                        >
                            <img
                                src={p.img}
                                alt={p.title}
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent opacity-90" />
                            <div className="absolute inset-0 p-5 flex flex-col justify-end text-left">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-gold">
                                    {p.category}
                                </div>
                                <div className="font-display text-lg md:text-xl text-white leading-tight mt-1">
                                    {p.title}
                                </div>
                            </div>
                            <div className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 backdrop-blur border border-white/20 grid place-items-center opacity-0 group-hover:opacity-100 transition">
                                <ArrowUpRight className="h-4 w-4 text-white" />
                            </div>
                        </motion.button>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {active && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm p-4 md:p-10 flex items-center justify-center"
                        onClick={() => setActive(null)}
                        data-testid="portfolio-lightbox"
                    >
                        <motion.div
                            initial={{ scale: 0.96, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.96, opacity: 0 }}
                            className="relative max-w-6xl w-full max-h-[90vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setActive(null)}
                                data-testid="lightbox-close"
                                className="absolute -top-12 right-0 text-white flex items-center gap-2"
                            >
                                Close <X className="h-4 w-4" />
                            </button>
                            <img
                                src={active.img}
                                alt={active.title}
                                className="w-full max-h-[80vh] object-contain rounded-lg"
                            />
                            <div className="mt-4 text-white">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-gold">
                                    {active.category}
                                </div>
                                <div className="font-display text-2xl">{active.title}</div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
